import React, { useEffect, useState, useMemo, useCallback } from "react";
import { MapContainer, TileLayer, Marker, useMap } from "react-leaflet";
import L from "leaflet";
import { useNavigate } from "react-router-dom";
import { LocateFixed, List, X, MapPin } from "lucide-react";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { localized } from "@/i18n";
import { CATEGORY_COLORS, VLC_CENTER, priceSymbol } from "@/constants";
import { toApiParams, getGeo } from "@/lib/filters";
import FilterSheet from "@/components/FilterSheet";
import ClusterLayer from "@/components/ClusterLayer";

function pinIcon(color, price, active) {
  const size = active ? 46 : 38;
  return L.divIcon({
    className: "vv-pin",
    html: `<div style="position:relative;transform:translate(-50%,-100%);">
      <div style="width:${size}px;height:${size}px;background:${color};border:3px solid #fff;border-radius:50% 50% 50% 0;transform:rotate(-45deg);box-shadow:0 3px 8px rgba(0,0,0,.35);display:flex;align-items:center;justify-content:center;">
        <span style="transform:rotate(45deg);color:#fff;font-weight:800;font-size:11px;font-family:monospace;">${priceSymbol(price)}</span>
      </div></div>`,
    iconSize: [size, size],
    iconAnchor: [0, 0],
  });
}

function Recenter({ target }) {
  const map = useMap();
  useEffect(() => { if (target) map.flyTo(target, 15, { duration: 1 }); }, [target, map]);
  return null;
}

export default function MapPage() {
  const { t, lang } = useApp();
  const navigate = useNavigate();
  const [places, setPlaces] = useState([]);
  const [filters, setFilters] = useState({ category: "", tags: [], price_max: 0, open_now: false, best_moment: "", sort: "" });
  const [selected, setSelected] = useState(null);
  const [userPos, setUserPos] = useState(null);
  const [recenter, setRecenter] = useState(null);

  const fetchPlaces = async (f) => {
    try {
      const { data } = await api.get("/places", { params: toApiParams(f, null) });
      setPlaces(data.filter((p) => p.lat && p.lng));
    } catch { setPlaces([]); }
  };

  useEffect(() => { fetchPlaces(filters); /* eslint-disable-next-line */ }, []);

  const applyFilters = (f) => { setFilters(f); setSelected(null); fetchPlaces(f); };

  const handleLocate = async () => {
    const geo = await getGeo();
    if (geo) { setUserPos([geo.lat, geo.lng]); setRecenter([geo.lat, geo.lng]); }
    else setRecenter([...VLC_CENTER]);
  };

  const userIcon = useMemo(() => L.divIcon({
    className: "vv-user",
    html: `<div style="width:18px;height:18px;background:#3A86EF;border:3px solid #fff;border-radius:50%;box-shadow:0 0 0 6px rgba(58,134,239,.25);"></div>`,
    iconSize: [18, 18], iconAnchor: [9, 9],
  }), []);

  const selectedId = selected?.id;
  const iconFor = useCallback((p) => pinIcon(CATEGORY_COLORS[p.category] || "#0F4C5C", p.price_level, selectedId === p.id), [selectedId]);

  return (
    <div className="relative" style={{ height: "calc(100dvh - 56px)" }}>
      <MapContainer center={VLC_CENTER} zoom={13} zoomControl={false} className="h-full w-full" style={{ zIndex: 0 }}>
        <TileLayer
          attribution='&copy; OpenStreetMap contributors'
          url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <Recenter target={recenter} />
        {userPos && <Marker position={userPos} icon={userIcon} />}
        <ClusterLayer places={places} iconFor={iconFor} onSelect={setSelected} />
      </MapContainer>

      {/* Top floating controls */}
      <div className="pointer-events-none absolute inset-x-0 top-0 z-[500] p-3">
        <div className="pointer-events-auto flex items-center justify-between gap-2">
          <FilterSheet filters={filters} onApply={applyFilters} />
          <span className="rounded-full bg-white/95 px-3 py-2 text-xs font-semibold text-ink shadow">{places.length} {t("results")}</span>
        </div>
      </div>

      {/* Right floating buttons */}
      <div className="absolute right-3 z-[500] flex flex-col gap-2" style={{ bottom: selected ? 220 : 96 }}>
        <button data-testid="map-locate" onClick={handleLocate} className="grid h-11 w-11 place-items-center rounded-full bg-white text-sea shadow-lg active:scale-95">
          <LocateFixed className="h-5 w-5" />
        </button>
        <button data-testid="map-to-list" onClick={() => navigate("/explore")} className="grid h-11 w-11 place-items-center rounded-full bg-sea text-white shadow-lg active:scale-95">
          <List className="h-5 w-5" />
        </button>
      </div>

      {/* Quick preview */}
      {selected && (
        <div data-testid="map-preview" className="absolute inset-x-3 z-[500]" style={{ bottom: 88 }}>
          <div className="relative flex gap-3 rounded-2xl bg-white p-3 shadow-2xl">
            <button data-testid="map-preview-close" aria-label={t("cancel")} onClick={() => setSelected(null)} className="absolute right-2 top-2 grid h-7 w-7 place-items-center rounded-full bg-sand text-ink-muted">
              <X className="h-4 w-4" />
            </button>
            <div className="h-20 w-20 shrink-0 overflow-hidden rounded-xl bg-sand">
              {selected.images?.[0] ? <img src={selected.images[0]} alt="" className="h-full w-full object-cover" /> : <div className="grid h-full w-full place-items-center"><MapPin className="h-6 w-6 text-terracotta/40" /></div>}
            </div>
            <div className="min-w-0 flex-1 pr-6">
              <h3 className="truncate font-heading font-bold text-ink">{selected.name}</h3>
              <p className="flex items-center gap-2 text-xs text-ink-muted">
                <span>{selected.neighborhood}</span>
                <span className="font-mono font-bold text-emerald-700">{priceSymbol(selected.price_level)}</span>
                {selected.open_now === true && <span className="text-emerald-600 font-semibold">{t("open")}</span>}
              </p>
              <p className="mt-1 line-clamp-2 text-xs text-ink-muted">{localized(selected.summary, lang)}</p>
              <button data-testid="map-preview-open" onClick={() => navigate(`/place/${selected.id}`)} className="mt-2 rounded-full bg-terracotta px-3.5 py-1.5 text-xs font-bold text-white">{t("quick_view")}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
